import React, { useCallback } from "react";
import CruiseItineraryPortItem from "./ItineraryItem/CruiseItineraryPortItem/CruiseItineraryPortItem";
import * as styles from "./CruiseItinerary.module.scss";
import { CruiseItineraryItemWithExcursions, PrePostItem } from "./types";
import { BlockContent } from "components/BlockContent";
import { dequal } from "dequal/lite";
import {
  BlockContentValue,
  isBlockContentValuePresent,
  useDrawer
} from "@silversea-ssc/ui-components-react";
import {
  FlightPackageContentFragment,
  ImageWithAltFragment
} from "types/graphql-types";
import CruiseItineraryPrePostItem from "src/page-components/cruise/itinerary/ItineraryItem/CruiseItineraryPrePostItem/CruiseItineraryPrePostItem";
import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";
import { FareCode } from "@silversea-ssc/web-fares";
import { useClientSideFeatureFlag } from "src/utils/feature-flags";
import { UnbundlingCruiseItineraryPrePostItem } from "./ItineraryItem/UnbundlingCruiseItineraryPrePostItem/UnbundlingCruiseItineraryPrePostItem";

const PortShorexDrawer = React.lazy(
  () => import("src/page-components/cruise/PortExcursionModal/PortShorexDrawer")
);

interface CruiseItineraryProps {
  itinerary: CruiseItineraryItemWithExcursions[];
  defaultPortImage?: ImageWithAltFragment;
  hideShorexesOnFirstDay?: boolean;
  hideShorexesOnLastDay?: boolean;
  overrideItineraryDisclaimer?: BlockContentValue;
  preItinerary: readonly PrePostItem[];
  postItinerary: readonly PrePostItem[];
  tokenValues?: TokenValues;
  shorexesActiveOnFares: FareCode[];
  itineraryDisclaimer: BlockContentValue | undefined;
  cruiseType: "classic" | "expedition";
  flightPackage?: FlightPackageContentFragment;
}

type OpenExcursionModalShorexArgs = {
  type: "shorex";
  shorexWebCode: string;
  duration?: number;
  price?: number;
  hideDurationAndCode: boolean;
  code: string | undefined;
};

type OpenExcursionModalArgs = OpenExcursionModalShorexArgs;

export type OpenExcursionModal = (args: OpenExcursionModalArgs) => void;

const CruiseItinerary: React.FC<
  React.PropsWithChildren<CruiseItineraryProps>
> = ({
  itinerary,
  defaultPortImage,
  hideShorexesOnFirstDay = false,
  hideShorexesOnLastDay = false,
  overrideItineraryDisclaimer,
  preItinerary,
  postItinerary,
  tokenValues,
  shorexesActiveOnFares,
  itineraryDisclaimer,
  cruiseType,
  flightPackage
}) => {
  const {
    closeDrawer,
    drawerData,
    isDrawerMountable,
    isDrowerOpen,
    openDrawer
  } = useDrawer<OpenExcursionModalArgs>(undefined);

  const itineraryDisclaimerContent = isBlockContentValuePresent(
    overrideItineraryDisclaimer
  )
    ? overrideItineraryDisclaimer
    : isBlockContentValuePresent(itineraryDisclaimer)
      ? itineraryDisclaimer
      : undefined;

  const openModalOrDrawer = useCallback(
    (data: OpenExcursionModalArgs | undefined) => {
      if (data) openDrawer(data);
    },
    [openDrawer]
  );

  const isUnbundling = useClientSideFeatureFlag("unbundling");
  return (
    <React.Fragment>
      <div
        data-content-name="section_pre_itinerary"
        className={styles.preContainer}
      >
        {isUnbundling ? (
          <UnbundlingCruiseItineraryPrePostItem
            items={preItinerary}
            flightPackage={flightPackage}
            tokenValues={tokenValues}
            prePostSection="PRE"
          />
        ) : (
          preItinerary.map((item) => (
            <CruiseItineraryPrePostItem
              item={item}
              key={`pre-${item.inclusionCode}-${item.date}`}
              tokenValues={tokenValues}
            />
          ))
        )}
      </div>
      <div data-content-name="section_itinerary">
        {itinerary.map((itineraryItem, index, values) => {
          const isFirst = index == 0;
          const isLast = index == values.length - 1;
          const hideShorexes =
            (hideShorexesOnFirstDay && isFirst) ||
            (hideShorexesOnLastDay && isLast);

          return (
            <CruiseItineraryPortItem
              key={itineraryItem._key}
              item={itineraryItem}
              openExcursionModal={openModalOrDrawer}
              hideShorexes={hideShorexes}
              shorexes={itineraryItem.shorexes}
              defaultPortImage={defaultPortImage}
              shorexesActiveOnFares={shorexesActiveOnFares}
            />
          );
        })}

        {isDrawerMountable && drawerData && (
          <React.Suspense fallback={<div></div>}>
            <PortShorexDrawer
              isOpen={isDrowerOpen}
              onRequestClose={closeDrawer}
              cruiseType={cruiseType}
              {...drawerData}
            />
          </React.Suspense>
        )}
      </div>
      <div
        data-content-name="section_post_itinerary"
        className={styles.postContainer}
      >
        {isUnbundling ? (
          <UnbundlingCruiseItineraryPrePostItem
            items={postItinerary}
            flightPackage={flightPackage}
            tokenValues={tokenValues}
            prePostSection="POST"
          />
        ) : (
          postItinerary.map((item) => (
            <CruiseItineraryPrePostItem
              item={item}
              key={`post-${item.inclusionCode}-${item.date}`}
              tokenValues={tokenValues}
            />
          ))
        )}
      </div>
      <div className={styles.shorexDisclaimer}>
        {isBlockContentValuePresent(itineraryDisclaimerContent) && (
          <BlockContent value={itineraryDisclaimerContent} />
        )}
      </div>
    </React.Fragment>
  );
};

export default React.memo(CruiseItinerary, dequal);
